/**
 * NewEntryFlow — fullscreen overlay-мастер ввода/правки записи отчёта (§4.2).
 *
 * После задачи 77 §1.1 — тонкая обёртка над surface-агностичным ядром
 * (`entry-editor/EntryEditorCore`) + worker-self-адаптером
 * (`entry-editor/selfFieldAdapter`). Публичный `NewEntryFlowProps` и все
 * `data-testid`-якоря (`field-entry-flow*`) сохранены без изменений —
 * `FieldTimesheetPage` и существующие тесты рабочего трогать не нужно.
 *
 * Открывается из `FieldTimesheetPage` (portal в `document.body`, не route —
 * URL не меняется). Логику шагов, draft, pills, валидации, close-confirm и
 * offline-механику см. в `entry-editor/*` (детальные комментарии там).
 *
 * Контейнер-режим (fullscreen ↔ правый док на десктопе) и surface-флаг —
 * вводятся в §2.1; здесь остаётся текущий fullscreen.
 */
import * as React from "react";
import { createPortal } from "react-dom";

import type { Project, TimesheetEntry, WorkType } from "@/api/field-timesheet";

import { EntryEditorCore } from "./entry-editor/EntryEditorCore";
import { useSelfFieldAdapter } from "./entry-editor/selfFieldAdapter";

export interface NewEntryFlowProps {
  mode: "create" | "edit";
  /** Обязательно в edit-mode. */
  initialEntry?: TimesheetEntry;
  recentProjects: readonly Project[];
  myWorkTypes: readonly WorkType[];
  /** Активная дата таймшита (для query-key invalidate + qs API). */
  activeDate: string;
  onClose: () => void;
  /** Вызывается после успешного POST/PUT с новой entry. */
  onSaved: (entry: TimesheetEntry) => void;
  /** Вызывается после успешного DELETE (edit-mode шаг 3). */
  onDeleted?: () => void;
}

export function NewEntryFlow(props: NewEntryFlowProps): React.ReactElement | null {
  if (typeof document === "undefined") return null;
  return createPortal(<NewEntryFlowContent {...props} />, document.body);
}

function NewEntryFlowContent({
  mode,
  initialEntry,
  recentProjects,
  myWorkTypes,
  activeDate,
  onClose,
  onSaved,
  onDeleted,
}: NewEntryFlowProps): React.ReactElement {
  const adapter = useSelfFieldAdapter({ activeDate, onClose, onSaved, onDeleted });
  return (
    <EntryEditorCore
      mode={mode}
      initialEntry={initialEntry}
      recentProjects={recentProjects}
      myWorkTypes={myWorkTypes}
      adapter={adapter}
      onClose={onClose}
    />
  );
}
